import React from 'react';
import Box from '@mui/material/Box';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, SubHead, InfoBox, WarnBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'overview',         label: 'SLA Overview' },
  { id: 'definitions',      label: 'Definitions' },
  { id: 'uptime',           label: 'Uptime Commitment' },
  { id: 'credits',          label: 'Service Credits' },
  { id: 'measurement',      label: 'Uptime Measurement' },
  { id: 'exclusions',       label: 'Exclusions' },
  { id: 'maintenance',      label: 'Planned Maintenance' },
  { id: 'support-sla',      label: 'Support Response SLA' },
  { id: 'claiming',         label: 'Claiming Credits' },
  { id: 'contact',          label: 'Contact' },
];

const related = [
  { label: 'Terms of Service', href: '/terms-of-service' },
  { label: 'Refund Policy',    href: '/refund-policy' },
  { label: 'Privacy Policy',   href: '/privacy-policy' },
];

interface CreditRowProps { downtime: string; credit: string; header?: boolean }
const CreditRow: React.FC<CreditRowProps> = ({ downtime, credit, header }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    borderBottom: '1px solid #e5e7eb',
    background: header ? '#1f2937' : 'transparent',
    '&:hover': header ? {} : { background: '#f9fafb' },
  }}>
    {[downtime, credit].map((cell, i) => (
      <Box key={i} sx={{
        px: 3, py: 1.5,
        fontSize: header ? '0.78rem' : '0.9rem',
        color: header ? '#f9fafb' : '#374151',
        fontWeight: header ? 700 : (i === 1 ? 600 : 400),
        textTransform: header ? 'uppercase' : 'none',
        letterSpacing: header ? '0.04em' : 'normal',
      }}>
        {cell}
      </Box>
    ))}
  </Box>
);

interface SupportRowProps { priority: string; scope: string; target: string; header?: boolean }
const SupportRow: React.FC<SupportRowProps> = ({ priority, scope, target, header }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1fr',
    borderBottom: '1px solid #e5e7eb',
    background: header ? '#1f2937' : 'transparent',
    '&:hover': header ? {} : { background: '#f9fafb' },
  }}>
    {[priority, scope, target].map((cell, i) => (
      <Box key={i} sx={{
        px: 2.5, py: 1.5,
        fontSize: header ? '0.78rem' : '0.875rem',
        color: header ? '#f9fafb' : (i === 2 ? '#dc2626' : '#374151'),
        fontWeight: header ? 700 : (i === 0 || i === 2 ? 600 : 400),
        textTransform: header ? 'uppercase' : 'none',
        letterSpacing: header ? '0.04em' : 'normal',
      }}>
        {cell}
      </Box>
    ))}
  </Box>
);

const SLAPage: React.FC = () => (
  <PolicyPageLayout
    title="Service Level Agreement"
    lastUpdated="April 26, 2026"
    effectiveDate="April 26, 2026"
    toc={toc}
    related={related}
  >

    <PolicySection id="overview" number="1" title="SLA Overview">
      <Para>
        This Service Level Agreement ("SLA") describes CruvzMail's commitments regarding the
        availability, performance, and support response of the email hosting platform operated by
        Cruvz Online Shopping India Pvt Ltd. This SLA forms part of the Terms of Service.
      </Para>
      <InfoBox>
        CruvzMail commits to a <strong>99.9% monthly uptime</strong> for core email services.
        If we fall below this target due to our infrastructure, you are entitled to service credits
        as described in this document.
      </InfoBox>
      <Para>
        This SLA applies to all paid subscription plans. Free trial accounts and accounts using
        Community tier features are not covered by this SLA.
      </Para>
    </PolicySection>

    <PolicySection id="definitions" number="2" title="Definitions">
      <BulletList>
        <BulletItem>
          <strong>"Monthly Uptime Percentage"</strong> — The total minutes in a calendar month minus
          Service Downtime minutes, divided by total minutes, expressed as a percentage.
        </BulletItem>
        <BulletItem>
          <strong>"Service Downtime"</strong> — A period during which the Service is completely
          unavailable or core email functions (send/receive/webmail login) are non-operational,
          as verified and acknowledged by CruvzMail. Does not include Scheduled Maintenance.
        </BulletItem>
        <BulletItem>
          <strong>"Scheduled Maintenance"</strong> — Planned maintenance windows announced at least
          48 hours in advance through the Status Page and in-app notifications.
        </BulletItem>
        <BulletItem>
          <strong>"Service Credit"</strong> — A credit applied to your account for future billing,
          calculated as a percentage of your monthly fee for the affected period.
        </BulletItem>
        <BulletItem>
          <strong>"Core Email Services"</strong> — Email sending (SMTP), receiving (IMAP), and
          webmail access. Calendar, tasks, notes, and DriveX are best-effort services.
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="uptime" number="3" title="Uptime Commitment">
      <Para>
        CruvzMail commits to the following Monthly Uptime Percentage targets for paid customers:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Core email services (SMTP/IMAP/Webmail):</strong> 99.9% monthly uptime
          (maximum 44 minutes of downtime per month)
        </BulletItem>
        <BulletItem>
          <strong>API endpoints:</strong> 99.9% monthly uptime
        </BulletItem>
        <BulletItem>
          <strong>DriveX file storage:</strong> 99.5% monthly uptime (best effort, not SLA-covered)
        </BulletItem>
        <BulletItem>
          <strong>AI features:</strong> Best effort (dependent on third-party AI provider availability)
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="credits" number="4" title="Service Credits">
      <Para>
        If the Monthly Uptime Percentage for Core Email Services falls below our commitment due to
        our infrastructure, you are entitled to the following service credits:
      </Para>
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', mt: 2, mb: 2 }}>
        <CreditRow header downtime="Monthly Downtime (minutes)" credit="Credit (% of monthly fee)" />
        <CreditRow downtime="0 – 44 minutes (within SLA)" credit="No credit" />
        <CreditRow downtime="45 min – 4.3 hours (99.0% – 99.9%)" credit="10% credit" />
        <CreditRow downtime="4.3 – 21.6 hours (99.0% – 97.0%)" credit="25% credit" />
        <CreditRow downtime="Over 21.6 hours (below 97.0%)" credit="50% credit" />
      </Box>
      <Para>
        Service credits are calculated as a percentage of the monthly fee for the affected service
        (or 1/12 of the annual fee for annual subscribers). Credits are applied to your next invoice
        and cannot be converted to cash.
      </Para>
      <WarnBox>
        Service credits are the sole and exclusive remedy for Service Downtime under this SLA. Maximum
        credits in any single calendar month are capped at 50% of your monthly fee for that month.
      </WarnBox>
    </PolicySection>

    <PolicySection id="measurement" number="5" title="Uptime Measurement">
      <Para>
        Uptime is measured using external and internal monitoring systems that check core service
        endpoints every minute from multiple locations. A downtime event is recorded when:
      </Para>
      <BulletList>
        <BulletItem>Three or more consecutive monitoring checks fail for a core service endpoint</BulletItem>
        <BulletItem>The failure is confirmed by CruvzMail's internal operations team</BulletItem>
        <BulletItem>No pre-announced Scheduled Maintenance window is in effect</BulletItem>
      </BulletList>
      <Para>
        Our public Status Page at <strong>status.cruvzmail.com</strong> displays real-time and
        historical service availability. Downtime records from our monitoring system are the
        authoritative source for SLA credit calculations.
      </Para>
    </PolicySection>

    <PolicySection id="exclusions" number="6" title="Exclusions from SLA">
      <Para>
        The following are excluded from Monthly Uptime calculations and do not count as Service Downtime:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Scheduled Maintenance:</strong> Planned maintenance windows announced at least
          48 hours in advance
        </BulletItem>
        <BulletItem>
          <strong>Customer-caused incidents:</strong> Downtime resulting from actions by the Customer,
          including misconfigured DNS, exceeding rate limits, or misuse of the API
        </BulletItem>
        <BulletItem>
          <strong>Force majeure:</strong> Outages caused by events beyond our reasonable control,
          including natural disasters, power grid failures, DDoS attacks, or internet backbone
          disruptions that affect our upstream providers
        </BulletItem>
        <BulletItem>
          <strong>Third-party services:</strong> Outages in services not operated by CruvzMail
          (e.g., Razorpay payment gateway, AI providers, or upstream DNS registrars)
        </BulletItem>
        <BulletItem>
          <strong>Beta/preview features:</strong> Any features explicitly labeled as beta, preview,
          or experimental
        </BulletItem>
        <BulletItem>
          <strong>Suspended accounts:</strong> Accounts suspended for AUP or Terms of Service
          violations
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="maintenance" number="7" title="Planned Maintenance">
      <Para>
        CruvzMail performs planned maintenance to apply security patches, infrastructure upgrades,
        and performance improvements. Our maintenance policy:
      </Para>
      <BulletList>
        <BulletItem>
          <strong>Advance notice:</strong> At least 48 hours notice via the Status Page
          and in-app notifications for standard maintenance; 7 days for extended maintenance windows
        </BulletItem>
        <BulletItem>
          <strong>Preferred windows:</strong> Maintenance is scheduled during off-peak hours
          (typically between 00:00–05:00 IST on weekdays or weekends)
        </BulletItem>
        <BulletItem>
          <strong>Duration:</strong> Standard maintenance windows are 30–60 minutes per session
        </BulletItem>
        <BulletItem>
          <strong>Emergency patches:</strong> Critical security patches may be applied with
          shorter notice (minimum 2 hours) when delay would create significant security risk
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="support-sla" number="8" title="Support Response SLA">
      <Para>
        CruvzMail provides support via email at support@cruvzmail.com. Response time targets by
        priority level:
      </Para>
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', mt: 2, mb: 2 }}>
        <SupportRow header priority="Priority" scope="Description" target="First Response" />
        <SupportRow priority="Critical (P1)" scope="Complete email service outage affecting all users in your organization" target="≤ 1 hour" />
        <SupportRow priority="High (P2)" scope="Major feature broken affecting most users; significant security issue" target="≤ 4 hours" />
        <SupportRow priority="Medium (P3)" scope="Feature degraded or working with workaround; single user impacted" target="≤ 8 business hours" />
        <SupportRow priority="Low (P4)" scope="Minor UI issue, feature request, billing inquiry, or general question" target="≤ 48 business hours" />
      </Box>
      <Para>
        Business hours are 09:00–18:00 IST, Monday–Friday, excluding Indian public holidays.
        Critical (P1) support is available 24x7 at critical@cruvzmail.com.
      </Para>
      <SubHead>Enterprise Support</SubHead>
      <Para>
        Enterprise plan customers receive a dedicated account manager, a private Slack/Teams channel
        for escalations, and a 30-minute SLA for critical incidents. Contact sales@cruvzmail.com for
        Enterprise support details.
      </Para>
    </PolicySection>

    <PolicySection id="claiming" number="9" title="Claiming Service Credits">
      <Para>To claim a service credit, you must:</Para>
      <BulletList>
        <BulletItem>
          Submit a credit request to billing@cruvzmail.com within <strong>30 days</strong> of the
          end of the calendar month in which the downtime occurred
        </BulletItem>
        <BulletItem>
          Include your organization name, the dates and times of the downtime experienced, and a
          description of the impact on your operations
        </BulletItem>
        <BulletItem>
          Reference the incident tracking number from the Status Page (if available)
        </BulletItem>
      </BulletList>
      <Para>
        CruvzMail will review your request against our monitoring records within 10 business days
        and either approve the credit, request additional information, or explain why the event does
        not qualify for a credit. Approved credits will appear on your next invoice.
      </Para>
    </PolicySection>

    <PolicySection id="contact" number="10" title="Contact">
      <Para>For SLA-related inquiries, incident reports, and credit claims:</Para>
      <ContactCard>
        <strong>Critical incidents (24x7):</strong> critical@cruvzmail.com<br />
        <strong>Billing &amp; credit claims:</strong> billing@cruvzmail.com<br />
        <strong>General support:</strong> support@cruvzmail.com<br />
        <strong>Status Page:</strong> status.cruvzmail.com<br />
        <br />
        <strong>CruvzMail</strong> — operated by Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default SLAPage;
